import { oc } from "@orpc/contract";
import { z } from "zod";

const DataAssetVersionSchema = z.object({
  id: z.uuid(),
  dataAssetId: z.uuid(),
  versionNumber: z.number(),
  status: z.enum(["pending_upload", "ready", "error"]),
  reason: z.string().nullable().optional(),
  contentType: z.string(),
  containerName: z.string(),
  blobKey: z.string(),
  fileSize: z.number().nullable().optional(),
  fileName: z.string(),
  uploadedBy: z.uuid(),
  createdAt: z.iso.datetime(),
});

export type DataAssetVersionResponse = z.infer<typeof DataAssetVersionSchema>;

const DataAssetSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  currentVersionId: z.uuid().nullable().optional(),
  createdBy: z.uuid(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  currentVersion: DataAssetVersionSchema.nullable().optional(),
});

export type DataAssetResponse = z.infer<typeof DataAssetSchema>;

const DataAssetWithVersionsSchema = DataAssetSchema.extend({
  versions: z.array(DataAssetVersionSchema),
});

export type DataAssetWithVersionsResponse = z.infer<
  typeof DataAssetWithVersionsSchema
>;

export const dataAssetContracts = {
  create: oc
    .input(
      z.object({
        name: z.string().min(1).max(255),
        description: z.string().optional(),
        fileName: z.string().min(1),
        contentType: z.string().min(1),
      }),
    )
    .output(
      z.object({
        dataAsset: DataAssetSchema,
        version: DataAssetVersionSchema,
        uploadUrl: z.string(),
      }),
    )
    .route({ method: "POST" }),

  confirmUpload: oc
    .input(
      z.object({
        versionId: z.uuid(),
        fileSize: z.number().optional(),
      }),
    )
    .output(
      z.object({
        dataAsset: DataAssetSchema,
        version: DataAssetVersionSchema,
      }),
    )
    .route({ method: "POST" }),

  list: oc
    .input(z.object({}))
    .output(z.object({ dataAssets: z.array(DataAssetSchema) }))
    .route({ method: "GET" }),

  get: oc
    .input(z.object({ id: z.uuid() }))
    .output(
      z.object({
        dataAsset: DataAssetWithVersionsSchema,
      }),
    )
    .route({ method: "GET" }),

  update: oc
    .input(
      z.object({
        id: z.uuid(),
        name: z.string().min(1).max(255).optional(),
        description: z.string().nullable().optional(),
      }),
    )
    .output(z.object({ dataAsset: DataAssetSchema }))
    .route({ method: "PUT" }),

  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),

  uploadVersion: oc
    .input(
      z.object({
        dataAssetId: z.uuid(),
        fileName: z.string().min(1),
        contentType: z.string().min(1),
        reason: z.string().optional(),
      }),
    )
    .output(
      z.object({
        version: DataAssetVersionSchema,
        uploadUrl: z.string(),
      }),
    )
    .route({ method: "POST" }),

  confirmVersionUpload: oc
    .input(
      z.object({
        versionId: z.uuid(),
        fileSize: z.number().optional(),
      }),
    )
    .output(
      z.object({
        dataAsset: DataAssetSchema,
        version: DataAssetVersionSchema,
      }),
    )
    .route({ method: "POST" }),

  revertVersion: oc
    .input(
      z.object({
        dataAssetId: z.uuid(),
        versionId: z.uuid(),
      }),
    )
    .output(z.object({ dataAsset: DataAssetSchema }))
    .route({ method: "POST" }),

  getVersionSignedUrl: oc
    .input(z.object({ versionId: z.uuid() }))
    .output(z.object({ downloadUrl: z.string() }))
    .route({ method: "GET" }),
} as const;
